import { UserSearch } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { dateOnly } from '@/lib/utils';
import { useFormScreen } from '@/components/ui/form-screen';
import { Badge, Button } from '@/components/ui/primitives';
import type { PersonMatch } from './PersonLookupPanel';

export function PlatformMatches({
  matches,
  onUse,
  onAssociate,
  associating,
}: {
  matches: PersonMatch[];
  onUse: (person: PersonMatch) => void;
  onAssociate: (person: PersonMatch) => void;
  associating: boolean;
}) {
  const { leave } = useFormScreen();
  return (
    <div className="rounded-lg border border-info/30 bg-info/[0.06] p-3 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1" aria-live="polite">
      <p className="flex items-center gap-2 text-sm font-medium">
        <UserSearch className="h-4 w-4 shrink-0 text-info" aria-hidden />
        {tr('opd', 'platform_match_title')}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">{tr('opd', 'platform_match_hint')}</p>
      <ul className="mt-2 space-y-2">
        {matches.map((person) => (
          <li key={person.personId} className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background px-3 py-2">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                <span className="truncate">{person.fullName}</span>
                {person.knownAtThisFacility && <Badge variant="success">{tr('opd', 'already_registered_here')}</Badge>}
              </p>
              <p className="text-xs text-muted-foreground" dir="ltr">
                {person.uhid}
                {person.dob ? ` · ${dateOnly(person.dob)}` : ''}
                {person.phone ? ` · ${person.phone}` : ''}
              </p>
            </div>
            {person.knownAtThisFacility && person.patientIdHere ? (
              <Button type="button" size="sm" variant="outline" onClick={() => leave(`/patients/${person.patientIdHere}`)}>
                {tr('opd', 'open_their_record')}
              </Button>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => onUse(person)}>
                  {tr('opd', 'use_these_details')}
                </Button>
                <Button type="button" size="sm" loading={associating} onClick={() => onAssociate(person)}>
                  {tr('opd', 'take_them_on')}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
